import { Alert, Box, Grid, List, MenuItem, Select, Snackbar, Typography } from "@mui/material";
import React, { useState, useContext, useEffect } from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CurrencySettings from "./CurrencySettings";
import Store from "../../../Store/Store";
import { Spinner } from "reactstrap";
import useEstablishmentSettings from "../../../hooks/useEstablishmentSettings";
import useEstablishmentUoMs from "../../../hooks/useEstablishmentUoMs";
import { useMutation, useQueryClient } from "react-query";
import inventoryClient from "../../../clients/inventoryClient";
import { unitOfMeasurementTypes } from "../../../clients/inventoryObjects";

const CostAnalysisSettings = ({ checkedStyle, defaultStyle }) => {
  const store = useContext(Store);
  const { data: currentCurrency, isLoading: loading } =
    useEstablishmentSettings(store.currentEstablishment?.id);
  const { data: uomData } = useEstablishmentUoMs(store.currentEstablishment?.id);
  const [selectedCurrency, setSelectedCurrency] = useState();
  const queryClient = useQueryClient();
  const [alertSuccess, setAlertSuccess] = useState(false);

  const volumetricUoMs = (uomData?.unitsOfMeasurement || []).filter(
    (u) => u.type === unitOfMeasurementTypes.Volumetric && !u.isKegSize
  );
  const massUoMs = (uomData?.unitsOfMeasurement || []).filter(
    (u) => u.type === unitOfMeasurementTypes.Mass
  );

  const handleCloseAlert = () => {
    setAlertSuccess(false);
  };
  const currencyData = [
    {
      id: 0,
      name: "American Dollar",
      description: "($)",
    },
    {
      id: 1,
      name: "Canadian Dollar",
      description: "($)",
    },
    {
      id: 2,
      name: "English Sterling",
      description: "(£)",
    },
  ];

  useEffect(() => {
    if (currentCurrency) {
      setSelectedCurrency(currentCurrency?.inventorySettings?.currency);
    }
  }, []);

  const settingsMutation = useMutation(inventoryClient.patchInventorySettings, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          "settings",
          {
            establishmentId: store.currentEstablishment?.id,
          },
        ],
      });
      queryClient.refetchQueries({
        queryKey: [
          "settings",
          {
            establishmentId: store.currentEstablishment?.id,
          },
        ],
      });
      setAlertSuccess(true);
    },
  });

  const handleCurrencyChange = (newCurrency) => {
    if (newCurrency !== currentCurrency) {
      let payload = {
        establishmentId: store.currentEstablishment?.id,
      };

      payload["CurrencyUpdate"] = { value: newCurrency };
      settingsMutation.mutate(payload);
      setSelectedCurrency(newCurrency);
    }
  };

  const handleVolumetricCostPerUoMChange = (e) => {
    const value = e.target.value === "" ? null : e.target.value;
    settingsMutation.mutate({
      establishmentId: store.currentEstablishment?.id,
      VolumetricCostPerUnitOfMeasurementIdUpdate: { value },
    });
  };

  const handleMassCostPerUoMChange = (e) => {
    const value = e.target.value === "" ? null : e.target.value;
    settingsMutation.mutate({
      establishmentId: store.currentEstablishment?.id,
      MassCostPerUnitOfMeasurementIdUpdate: { value },
    });
  };

  if (loading) {
    return <Spinner />;
  }

  return (
    <>
      <Snackbar
        open={alertSuccess}
        onClose={handleCloseAlert}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success" variant="filled">
          Success! Establishment settings updated.
        </Alert>
      </Snackbar>
      <Box>
        <Grid
          container
          sx={{ display: "flex", justifyContent: "space-between" }}
        >
          <Grid item xs={10}>
            <Typography variant="h4">Currency</Typography>
          </Grid>
          <Grid
            item
            xs={2}
            sx={{ display: "flex", justifyContent: "flex-end" }}
          >
            <InfoOutlinedIcon
              sx={{
                color: (theme) => theme.palette.primary[800],
                cursor: "pointer",
              }}
            />
          </Grid>
        </Grid>
        <Box sx={{ marginTop: 4 }}>
          <List>
            {currencyData.map((x) => (
              <CurrencySettings
                key={x.name}
                checkedStyle={checkedStyle}
                defaultStyle={defaultStyle}
                data={x}
                currentCurrency={currentCurrency}
                handleSelect={handleCurrencyChange}
                selectedCurrency={selectedCurrency}
                disabled={settingsMutation?.isLoading}
              />
            ))}
          </List>
        </Box>
      </Box>
      <Box sx={{ marginTop: 8 }}>
        <Typography variant="h4">Cost Per Unit of Measurement</Typography>
        <Typography variant="body2" sx={{ marginTop: 2 }}>
          Choose which unit of measurement to use when calculating cost per unit
          for your product formats in the product library.
        </Typography>
        <Grid container spacing={4} sx={{ marginTop: 2 }}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" sx={{ marginBottom: 1 }}>
              Volumetric
            </Typography>
            <Select
              fullWidth
              size="small"
              value={
                currentCurrency?.inventorySettings
                  ?.volumetricCostPerUnitOfMeasurementId || ""
              }
              onChange={handleVolumetricCostPerUoMChange}
              disabled={settingsMutation?.isLoading}
              displayEmpty
            >
              <MenuItem value="">
                <em>Default (fl. oz)</em>
              </MenuItem>
              {volumetricUoMs.map((uom) => (
                <MenuItem key={uom.id} value={uom.id}>
                  {uom.name} ({uom.abbreviation})
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" sx={{ marginBottom: 1 }}>
              Mass
            </Typography>
            <Select
              fullWidth
              size="small"
              value={
                currentCurrency?.inventorySettings
                  ?.massCostPerUnitOfMeasurementId || ""
              }
              onChange={handleMassCostPerUoMChange}
              disabled={settingsMutation?.isLoading}
              displayEmpty
            >
              <MenuItem value="">
                <em>Default (oz)</em>
              </MenuItem>
              {massUoMs.map((uom) => (
                <MenuItem key={uom.id} value={uom.id}>
                  {uom.name} ({uom.abbreviation})
                </MenuItem>
              ))}
            </Select>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default CostAnalysisSettings;
